import { ReactNode } from "react";

import { EnumOptionsBE } from "src/api/flowTypes";
import {
  ARRAY_ERROR_MESSAGE,
  BOOLEAN_ERROR_MESSAGE,
  DATETIME_ERROR_MESSAGE,
  DATE_ERROR_MESSAGE,
  INTEGER_ERROR_MESSAGE,
  NUMBER_ERROR_MESSAGE,
  OBJECT_ERROR_MESSAGE,
  STRING_ERROR_MESSAGE,
} from "src/datasets/DatasetTable/validationErrorMessages";
import { isISO8601DateTimeString, isISO8601String } from "src/utils/datetime";

export type ValidationResult = {
  message: string;
  description?: ReactNode;
  applyFix?: (value: string) => string;
};

type ValidatorFunc = (value: string) => ValidationResult | undefined;

export const validateBoolean: ValidatorFunc = (value) => {
  try {
    if (typeof JSON.parse(value) !== "boolean") {
      return BOOLEAN_ERROR_MESSAGE;
    }
  } catch {
    return BOOLEAN_ERROR_MESSAGE;
  }
};

export const validateString: ValidatorFunc = (value) => {
  try {
    if (typeof JSON.parse(value) !== "string") {
      return STRING_ERROR_MESSAGE;
    }
  } catch {
    return {
      message: STRING_ERROR_MESSAGE.message,
      description: (
        <>
          Ensure that the string value is enclosed within quotes. Were you
          intending to use{" "}
          <code className="font-fira-code inline-block rounded bg-gray-700 px-1 py-0.5">
            "{value}"
          </code>
          ?
        </>
      ),
      applyFix: (value) => `"${value}"`,
    };
  }
};

// YYYY-DDD or YYYYDDD
const ISO_ORDINAL_DATE_REGEXP = /^\d{4}-?\d{3}$/;

export const validateDate: ValidatorFunc = (value) => {
  try {
    const date = JSON.parse(value);
    if (
      typeof date !== "string" ||
      // We only support YYYY-MM-DD format
      date.length !== 10 ||
      !(
        isISO8601String(date) &&
        // Is valid ISO 8601 date but not ISO 8601 ordinal date
        !ISO_ORDINAL_DATE_REGEXP.test(date)
      )
    ) {
      return DATE_ERROR_MESSAGE;
    }
  } catch {
    return DATE_ERROR_MESSAGE;
  }
};

export const validateDatetime: ValidatorFunc = (value) => {
  try {
    const date = JSON.parse(value);
    if (
      typeof date !== "string" ||
      !(
        // Check if the date matches exact format: YYYY-MM-DDTHH:MM:SS(Z|+HH:MM/-HH:MM)
        (
          isISO8601DateTimeString(date) &&
          // Is valid ISO 8601 date but not ISO 8601 ordinal date
          !ISO_ORDINAL_DATE_REGEXP.test(date)
        )
      )
    ) {
      return DATETIME_ERROR_MESSAGE;
    }
  } catch {
    return DATETIME_ERROR_MESSAGE;
  }
};

export const validateAny: ValidatorFunc = (value: string) => {
  try {
    // if we can parse it, it's valid
    JSON.parse(value);
  } catch {
    return {
      message: "Invalid value",
      description: "Must be a valid value",
    };
  }
};

export const validateArray: ValidatorFunc = (value) => {
  try {
    if (!Array.isArray(JSON.parse(value))) {
      return ARRAY_ERROR_MESSAGE;
    }
  } catch {
    return ARRAY_ERROR_MESSAGE;
  }
};

export const validateObject: ValidatorFunc = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      parsed === null
    ) {
      return OBJECT_ERROR_MESSAGE;
    }
  } catch {
    return OBJECT_ERROR_MESSAGE;
  }
};

export const validateInteger: ValidatorFunc = (value) => {
  try {
    if (!Number.isInteger(JSON.parse(value))) {
      return INTEGER_ERROR_MESSAGE;
    }
  } catch {
    return INTEGER_ERROR_MESSAGE;
  }
};

export const validateNumber: ValidatorFunc = (value) => {
  try {
    const parsedValue = JSON.parse(value);
    if (typeof parsedValue !== "number" || Number.isNaN(parsedValue)) {
      return NUMBER_ERROR_MESSAGE;
    }
  } catch {
    return NUMBER_ERROR_MESSAGE;
  }
};

export const validateEnum = (
  value: EnumOptionsBE[number],
  enumValues?: EnumOptionsBE | null,
) => {
  if (!enumValues?.includes(value)) {
    return {
      message: "Invalid value",
      description: enumValues
        ? `Must be one of: ${enumValues.join(", ")}`
        : "Add an enum field to your schema to validate this value",
    };
  }
};
